import { map } from "../../utils/lodash";
import { BlockModel } from "../BlockModel";
import { QuestionnaireModel } from "../QuestionnaireModel";
import { ShortcutModel } from "../ShortcutModel";
import { ThemeModel } from "../ThemeModel";
import { createElementWithSettings, createSkippable, createTranslatable } from "./elementFactory";
import { createQuestion, getVariableDependencies } from "./questionFactory";
import { createLogic } from "./logicFactory";
import { ChoiceModel } from "../ChoiceModel";
import { createRandomization } from "./randomizationFactory";
import { ShortcutExitToType } from "../enums/ShortcutExitToType";
import { SurveyQuestionnaireModality } from "../enums/SurveyQuestionnaireModality";

/**
 * Updates variable dependencies for all questions in the questionnaire.
 * Called after the questionnaire is fully built to provide proper context for discrete range expansion.
 */
function updateQuestionVariableDependencies(questionnaire: QuestionnaireModel): void {
    if (!questionnaire || !questionnaire.blocks) return;

    for (const block of questionnaire.blocks) {
        if (!block.questions) continue;

        for (const question of block.questions) {
            // Update variable dependencies with full questionnaire context
            question.variableDependencies = getVariableDependencies(question, questionnaire);
        }
    }
}

export function createQuestionnaire(el?: any): QuestionnaireModel {
    if (el == null) return null;
    const choiceLists = map(el.choiceLists, (cl) =>
        (cl as any[]).map((c, i) => createChoice(i, c)),
    );
    const questionnaire: QuestionnaireModel = {
        ...createElementWithSettings(el),
        ...createTranslatable(el),
        _v: el._v,
        _d: el._d,
        id: el.id,
        name: el.name,
        surveyId: el.surveyId || el.id,
        // eslint-disable-next-line no-undef
        modality: el.modality || SurveyQuestionnaireModality.Master,
        version: el.version || 0,
        blocks: map(el.blocks, (b, i: number) => createBlock(i, choiceLists, b)),
        theme: createTheme({ ...el.theme, isDefault: true }),
        defaultLanguage: el.defaultLanguage,
        languages: el.languages,
        shortcuts: map(el.shortcuts, createShortcut),
        randomizations: map(el.randomizations, createRandomization),
        columns: el.columns,
        surveyProperties: el.surveyProperties,
        securityQuestionsList: el.securityQuestionsList
    };

    // Post-processing: Update variable dependencies after questionnaire is fully built
    updateQuestionVariableDependencies(questionnaire);

    return questionnaire;
}

export function blockIdByName(questionnaire: QuestionnaireModel) {
    const result: { [name: string]: number } = {};
    for (const block of questionnaire.blocks) result[block.name.toUpperCase()] = block.index;
    return result;
}

export function questionIdByName(questionnaire: QuestionnaireModel) {
    const result: { [name: string]: number[] } = {};
    for (const block of questionnaire.blocks) {
        for (const question of block.questions) {
            result[question.name.toUpperCase()] = [question.blockIndex, question.index];
        }
    }
    return result;
}

export function questionIdByVariable(questionnaire: QuestionnaireModel) {
    const result: { [name: string]: number[] } = {};
    for (const block of questionnaire.blocks) {
        for (const question of block.questions) {
            for (const variable of question.variableNames) {
                result[variable.toUpperCase()] = [question.blockIndex, question.index];
            }
        }
    }
    return result;
}

function createChoice(index: number, el?: any): ChoiceModel {
    if (el == null) return undefined;
    return {
        ...createElementWithSettings(el),
        ...createSkippable(el),
        ...createTranslatable(el),
        elementId: "",
        index,
        value: el.value,
        name: `CHOICE_${index}`,
        parentValue: el.parentValue,
        openEndDisplayLogic: createLogic(el.openEndDisplayLogic),
        position: el.position,
    };
}

function createShortcut(el?: any): ShortcutModel {
    if (el == null) return;
    return {
        ...createTranslatable(el),
        blockName: el.blockName,
        type: ShortcutExitToType[String(el.type)],
    };
}

export function createTheme(el?: any): ThemeModel {
    if (el == null) return;
    return {
        ...createElementWithSettings(el),
        ...createTranslatable(el),
        id: el.id,
        isDefault: el.isDefault,
        isPreview: el.isPreview,
        css: el.css,
        javascript: el.javascript,
        head: el.head,
    };
}

function createBlock(index: number, choiceLists: ChoiceModel[][], el?: any): BlockModel {
    if (el == null) return;
    return {
        ...createElementWithSettings(el),
        ...createTranslatable(el),
        id: el.id,
        index,
        questions: map(el.questions, (q, i: number) => createQuestion(i, index, choiceLists, q)),
        randomizations: el.randomizations.map(createRandomization),
        theme: createTheme(el.theme),
        skipLogic: createLogic(el.skipLogic),
        displayLogic: createLogic(el.displayLogic),
    };
}
